import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import SessionTimeoutManager from './components/common/SessionTimeout/SessionTimeoutManager';
import { PageLoaderProvider } from './components/common/PageLoader/PageLoader';


// CHECK THIS PATH: src/components/visual/Globe/Globe.js
import Globe from './components/visual/Globe/Globe';

// CHECK THIS PATH: src/features/auth/components/AuthForms.js
import {
    LoginForm,
    SignUpForm,
    SuccessView,
    UpdatePasswordForm,
    ForgotPasswordForm,
    ResetPasswordForm,
    MFAChoiceForm,
    MFASetupQRForm,
    MFAVerifyAuthenticatorForm,
    MFAVerifyEmailForm
} from './features/auth/components/AuthForms';
import { SetPasswordForm } from './features/auth/components/SetPasswordForm';

// Icons for left side features
import { TrendingUp, Cpu, Workflow, Leaf, ShieldCheck, Activity, Layers, CloudLightning } from 'lucide-react';
import AccelLogo from './assets/accel-logo.png';
import PolesImage from './assets/poles.png';

// Home
import Home from './pages/Home';

// Dashboard
import Dashboard from './pages/Dashboard';

// Assets
import Assets from './pages/Assets';

// Sites
import Sites from './pages/Sites';

// Locations
import Locations from './pages/Locations';

// Categories
import Categories from './pages/Categories';

// Assets List
import AssetsList from './pages/AssetsList';

// Design Components
import DesignComponents from './pages/DesignComponents';

// Pole Designs
import PoleDesigns from './pages/PoleDesigns';

// Supplier List
import SupplierList from './pages/SupplierList';

// Assets Dashboard
import AssetsDashboard from './pages/AssetsDashboard';
import Maintenance from './pages/Maintenance';
import Analytics from './pages/Analytics';

// Services (public page - no auth required)
// import Services from './pages/Services';

// CHECK THIS PATH: src/App.module.css
import styles from './App.module.css';
import Logo from './assets/hyderotraceos.png';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
    const accessToken = localStorage.getItem('accessToken');
    const userStr = localStorage.getItem('user');

    if (!accessToken || !userStr) {
        return <Navigate to="/signin" replace />;
    }

    let user;
    try {
        user = JSON.parse(userStr);
    } catch (e) {
        user = null;
    }

    if (!user) {
        return <Navigate to="/signin" replace />;
    }

    const userRole = user.role;
    const normUserRole = userRole ? userRole.replace(/[-_ ]/g, '').toLowerCase() : '';

    if (allowedRoles) {
        const normAllowed = allowedRoles.map(r => r.replace(/[-_ ]/g, '').toLowerCase());
        if (normUserRole !== 'technicalsupport' && normUserRole !== 'superadmin' && !normAllowed.includes(normUserRole)) {
            return <Navigate to="/home" replace />;
        }
    }

    return children;
}

// Public Route Component - redirects logged-in users to home
function PublicRoute({ children }) {
    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');

    if (accessToken && user) {
        return <Navigate to="/home" replace />;
    }

    return children;
}

// Auth Pages Wrapper Component
function AuthPages() {
    const navigate = useNavigate();
    const [view, setView] = useState('signin');
    const [userEmail, setUserEmail] = useState('');
    const [activationToken, setActivationToken] = useState('');
    const [resetPasswordData, setResetPasswordData] = useState({ uid: '', token: '' });
    const [mfaToken, setMfaToken] = useState('');
    const [qrCode, setQrCode] = useState('');

    // Handle navigation with custom setView function
    const handleSetView = (newView, email = '') => {
        if (email) {
            setUserEmail(email);
        }

        // Map views to routes
        const routeMap = {
            'signin': '/signin',
            'signup': '/signup',
            'forgot': '/forgot-password',
            'home': '/home',
            'dashboard': '/dashboard',
            // 'services': '/services'
        };

        if (routeMap[newView]) {
            navigate(routeMap[newView]);
        } else {
            setView(newView);
        }
    };

    const handleSetViewWithEmail = (newView, email = '') => {
        if (email) {
            setUserEmail(email);
        }
        handleSetView(newView);
    };

    const handleLoginSuccess = (loginData, email) => {
        setUserEmail(email);
        setMfaToken(loginData.mfa_token);

        if (loginData.mfa_configured) {
            setView('mfa-verify-authenticator');
        } else {
            setView('mfa-choice');
        }
    };

    // Check for reset password token in URL on mount
    useEffect(() => {
        const resetMatch = window.location.pathname.match(/\/reset-password\/([^\/]+)\/([^\/]+)/i);
        if (resetMatch) {
            const uid = resetMatch[1];
            const token = resetMatch[2];
            // console.log('Reset password token found - uid:', uid, 'token:', token);
            setResetPasswordData({ uid, token });
            setView('resetpassword');
            return;
        }

        // Check for activation token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromQuery = urlParams.get('token');
        const pathMatch = window.location.pathname.match(/\/activate\/([a-f0-9\-]+)/i);
        const tokenFromPath = pathMatch ? pathMatch[1] : null;
        const token = tokenFromQuery || tokenFromPath;

        if (token) {
            // console.log('Activation token found:', token);
            setActivationToken(token);
            setView('updatepassword');
        }
    }, []);

    const location = useLocation();

    // Determine which view to show based on URL
    useEffect(() => {
        const path = location.pathname;
        if (path === '/signup') setView('signup');
        else if (path === '/signin' || path === '/') setView('signin');
        else if (path === '/forgot-password') setView('forgot');
    }, [location]);

    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <div className={styles.leftContent}>
                    <div className={styles.logoWrap}>
                        <img src={AccelLogo} alt="ACCEL 1" className={styles.accelLogo} />
                    </div>
                    <div className={styles.brandTitles}>
                        <h1 className={styles.brandTitle}>One Platform.</h1>
                        <h1 className={styles.brandTitle}>Every Climate Function.</h1>
                        <h1 className={styles.brandSubtitle}>
                            <span className={styles.cyanText}>Smarter Cities.</span> <span className={styles.purpleText}>Cleaner Future.</span>
                        </h1>
                    </div>
                    <p className={styles.brandDesc}>
                        AtmosOS unifies infrastructure, intelligence, and operations across AtmosPOLE networks, satellites, and environmental systems — empowering cities and governments with real-time insights, automation, and AI-driven decisions.
                    </p>

                    {/* Visual representation of the climate poles */}
                    <div className={styles.polesContainer}>
                        <img src={PolesImage} alt="Climate Poles" className={styles.polesImage} />
                    </div>

                    <div className={styles.leftFeaturesGrid}>
                        <div className={styles.gridItem}>
                            <TrendingUp size={20} className={styles.gridIcon} />
                            <span>Real-time Intelligence</span>
                        </div>
                        <div className={styles.gridItem}>
                            <Cpu size={20} className={styles.gridIcon} />
                            <span>AI-Powered Decisions</span>
                        </div>
                        <div className={styles.gridItem}>
                            <Workflow size={20} className={styles.gridIcon} />
                            <span>Unified Operations</span>
                        </div>
                        <div className={styles.gridItem}>
                            <Leaf size={20} className={styles.gridIcon} />
                            <span>Sustainable Impact</span>
                        </div>
                        <div className={styles.gridItem}>
                            <ShieldCheck size={20} className={styles.gridIcon} />
                            <span>Secure & Compliant</span>
                        </div>
                        <div className={styles.gridItem}>
                            <Activity size={20} className={styles.gridIcon} />
                            <span>Scalable & Future Ready</span>
                        </div>
                        <div className={styles.gridItem}>
                            <Layers size={20} className={styles.gridIcon} />
                            <span>Interoperable Platform</span>
                        </div>
                        <div className={styles.gridItem}>
                            <CloudLightning size={20} className={styles.gridIcon} />
                            <span>Open APIs & Integrations</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.glassCard}>
                {view !== 'success' && view !== 'updatepassword' && view !== 'resetpassword' && (
                    <div className={styles.header}>
                        <h2 className={styles.atmosOSLogo}>
                            Atmos<span className={styles.cyanText}>OS</span><sup className={styles.tmSup}>TM</sup>
                        </h2>

                        {(view === 'signin' || view === 'signup') && (
                            <div className={styles.buttonWrap}>
                                <div className={`${styles.tabs} ${styles.newclass}`}>
                                    <button className={view === 'signin' ? styles.activeTab : styles.tabBtn} onClick={() => handleSetView('signin')}>Sign In</button>
                                    <button className={view === 'signup' ? styles.activeTab : styles.tabBtn} onClick={() => handleSetView('signup')}>Sign Up</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {view === 'signin' && <LoginForm key="1" setView={handleSetViewWithEmail} onLoginSuccess={handleLoginSuccess} />}
                    {view === 'signup' && <SignUpForm key="2" setView={handleSetView} />}
                    {view === 'forgot' && <ForgotPasswordForm key="3" setView={handleSetView} />}
                    {view === 'updatepassword' && <UpdatePasswordForm key="5" setView={handleSetView} token={activationToken} />}
                    {view === 'resetpassword' && <ResetPasswordForm key="6" setView={handleSetView} uid={resetPasswordData.uid} token={resetPasswordData.token} />}
                    {view === 'setpassword' && <SetPasswordForm key="5b" setView={handleSetView} activationToken={activationToken} />}
                    {view === 'success' && <SuccessView key="7" setView={handleSetView} />}
                    {view === 'mfa-choice' && <MFAChoiceForm key="mfa-choice" setView={handleSetView} mfaToken={mfaToken} email={userEmail} setQrCode={setQrCode} />}
                    {view === 'mfa-setup-qr' && <MFASetupQRForm key="mfa-setup-qr" setView={handleSetView} mfaToken={mfaToken} qrCode={qrCode} email={userEmail} />}
                    {view === 'mfa-verify-authenticator' && <MFAVerifyAuthenticatorForm key="mfa-verify-authenticator" setView={handleSetView} mfaToken={mfaToken} email={userEmail} />}
                    {view === 'mfa-verify-email' && <MFAVerifyEmailForm key="mfa-verify-email" setView={handleSetView} mfaToken={mfaToken} email={userEmail} />}
                </AnimatePresence>
            </div>
        </div>
    );
}

// Disable browser's async scroll restoration which causes sudden jumps on page changes
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

// ScrollToTop: Executes synchronously BEFORE the browser paints the new route.
// This completely eliminates any visual snap, flicker, or layout shift.
function ScrollToTop() {
    const { pathname } = useLocation();

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
        const mainEl = document.querySelector(`.${styles.main}`);
        if (mainEl && mainEl.scrollTop !== 0) {
            mainEl.scrollTop = 0;
        }
    }, [pathname]);

    return null;
}

// Navigation router helper - purely navigates, never touches scroll directly
const navigateView = (navigate, view) => {
    if (view === 'signin') navigate('/signin');
    else if (view === 'home') navigate('/home');
    else if (view === 'dashboard') navigate('/dashboard');
    else if (view === 'assets-dashboard') navigate('/assets-dashboard');
    else if (view === 'assets' || view === 'asset' || view === 'assets-list') navigate('/assets-list');
    else if (view === 'sites') navigate('/sites');
    else if (view === 'locations') navigate('/locations');
    else if (view === 'categories') navigate('/categories');
    else if (view === 'design-components' || view === 'components') navigate('/design-components');
    else if (view === 'pole-designs' || view === 'pole-design') navigate('/pole-designs');
    else if (view === 'supplier-list' || view === 'suppliers' || view === 'supplier') navigate('/supplier-list');
    else if (view === 'maintenance') navigate('/maintenance');
    else if (view === 'analytics') navigate('/analytics');
};

// Home Wrapper
function HomeWrapper() {
    const navigate = useNavigate();
    return <Home setView={(view) => navigateView(navigate, view)} />;
}

// Dashboard Wrapper
function DashboardWrapper() {
    const navigate = useNavigate();
    return <Dashboard setView={(view) => navigateView(navigate, view)} />;
}

// Assets Wrapper
function AssetsWrapper() {
    const navigate = useNavigate();
    return <Assets setView={(view) => navigateView(navigate, view)} />;
}

// Assets Dashboard Wrapper
function AssetsDashboardWrapper() {
    const navigate = useNavigate();
    return <AssetsDashboard setView={(view) => navigateView(navigate, view)} />;
}

// Sites Wrapper
function SitesWrapper() {
    const navigate = useNavigate();
    return <Sites setView={(view) => navigateView(navigate, view)} />;
}

// Locations Wrapper
function LocationsWrapper() {
    const navigate = useNavigate();
    return <Locations setView={(view) => navigateView(navigate, view)} />;
}

// Categories Wrapper
function CategoriesWrapper() {
    const navigate = useNavigate();
    return <Categories setView={(view) => navigateView(navigate, view)} />;
}

// Assets List Wrapper
function AssetsListWrapper() {
    const navigate = useNavigate();
    return <AssetsList setView={(view) => navigateView(navigate, view)} />;
}

// Design Components Wrapper
function DesignComponentsWrapper() {
    const navigate = useNavigate();
    return <DesignComponents setView={(view) => navigateView(navigate, view)} />;
}

// Pole Designs Wrapper
function PoleDesignsWrapper() {
    const navigate = useNavigate();
    return <PoleDesigns setView={(view) => navigateView(navigate, view)} />;
}

// Supplier List Wrapper
function SupplierListWrapper() {
    const navigate = useNavigate();
    return <SupplierList setView={(view) => navigateView(navigate, view)} />;
}

// Maintenance Wrapper
function MaintenanceWrapper() {
    const navigate = useNavigate();
    return <Maintenance setView={(view) => navigateView(navigate, view)} />;
}

// Analytics Wrapper
function AnalyticsWrapper() {
    const navigate = useNavigate();
    return <Analytics setView={(view) => navigateView(navigate, view)} />;
}


function App() {
    return (
        <Router>
            <PageLoaderProvider>
                <ScrollToTop />
                <SessionTimeoutManager />
                <div className={styles.main}>
                <div className={styles.background}></div>
                <div className={styles.overlay}></div>

                <Routes>
                    {/* Public Routes */}
                    <Route path="/signin" element={<PublicRoute><AuthPages /></PublicRoute>} />
                    <Route path="/signup" element={<PublicRoute><AuthPages /></PublicRoute>} />
                    <Route path="/forgot-password" element={<PublicRoute><AuthPages /></PublicRoute>} />
                    <Route path="/activate/:token" element={<AuthPages />} />
                    <Route path="/reset-password/:uid/:token" element={<AuthPages />} />

                    {/* Protected Routes */}
                    <Route
                        path="/home"
                        element={
                            <ProtectedRoute>
                                <HomeWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/assets"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <AssetsListWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/assets-dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <AssetsDashboardWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/sites"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <SitesWrapper />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/maintenance"
                        element={
                            <ProtectedRoute allowedRoles={['Maintenance', 'Research']}>
                                <MaintenanceWrapper />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/analytics"
                        element={
                            <ProtectedRoute allowedRoles={['Super Admin']}>
                                <AnalyticsWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/locations"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <LocationsWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/categories"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <CategoriesWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/design-components"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <DesignComponentsWrapper />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/components"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <DesignComponentsWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/pole-designs"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <PoleDesignsWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/supplier-list"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <SupplierListWrapper />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/suppliers"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <SupplierListWrapper />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/assets-list"
                        element={
                            <ProtectedRoute allowedRoles={['Asset-management']}>
                                <AssetsListWrapper />
                            </ProtectedRoute>
                        }
                    />

                    {/* Default Route */}
                    <Route path="/" element={<Navigate to="/home" replace />} />
                </Routes>
            </div>
            </PageLoaderProvider>
        </Router>
    );
}

export default App;
